import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User, Menu, LogOut, Search, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, CATEGORIES } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 glass-nav border-b border-purple-100">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="Ciara's Kidz" className="h-14 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {CATEGORIES.map((c) => (
              <Link
                key={c.key}
                to={`/shop/${c.key}`}
                data-testid={`nav-category-${c.key}`}
                className="px-3 py-2 rounded-full text-sm font-medium text-gray-700 hover:text-fuchsia-600 hover:bg-fuchsia-50 transition-colors"
              >
                {c.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <Link
              to="/shop"
              data-testid="nav-shop-all"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-gray-700 hover:bg-fuchsia-50"
            >
              <Search className="w-4 h-4" /> Shop All
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="account-menu-trigger"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-white brand-gradient hover:opacity-90 transition"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline max-w-[90px] truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-white">
                  {user.role === "admin" ? (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      Admin Dashboard
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}>
                      My Account & Orders
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-purple-800 hover:bg-purple-50"
              >
                <User className="w-4 h-4" /> <span className="hidden sm:inline">Login</span>
              </Link>
            )}

            <button
              data-testid="cart-drawer-toggle"
              onClick={() => setOpen(true)}
              className="relative p-2.5 rounded-full hover:bg-fuchsia-50 transition"
            >
              <ShoppingBag className="w-5 h-5 text-purple-900" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full brand-gradient text-white text-[11px] font-bold flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-toggle" className="lg:hidden p-2.5 rounded-full hover:bg-fuchsia-50">
                  <Menu className="w-5 h-5 text-purple-900" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white w-80">
                <div className="flex items-center justify-between mb-6">
                  <img src={LOGO_URL} alt="Ciara's Kidz" className="h-12 w-auto" />
                  <button onClick={() => setMobileOpen(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-col gap-1">
                  <Link to="/shop" onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-xl font-semibold hover:bg-fuchsia-50">
                    Shop All
                  </Link>
                  {CATEGORIES.map((c) => (
                    <Link
                      key={c.key}
                      to={`/shop/${c.key}`}
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 rounded-xl hover:bg-fuchsia-50 flex items-center justify-between"
                    >
                      <span className="font-medium">{c.label}</span>
                      <span className="text-xs text-muted-foreground">{c.sub}</span>
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
